import { ChatMessage, ChatSession, ConversationStore, MessageRole, RecordExchangeInput, ToolStep } from "../types";
import { PARROT_API_BASE_URL } from "../config";

/** Default (and only) history API host. Baked in — see `PARROT_API_BASE_URL`. */
export const PARROT_HISTORY_BASE_URL = `${PARROT_API_BASE_URL}/chatbot`;

export interface ParrotHistoryStoreConfig {
	/** Agent identifier used to scope sessions (e.g. "plexie"). */
	agentType: string;
	/** JWT used both for auth (x-auth-token) and to derive the user's email. */
	authToken?: string;
	/** The signed-in user's email, scoping which sessions are read and written. Takes precedence
	 *  over the email decoded from `authToken`.
	 *
	 *  Bridge for the SSO migration: a platform session token is opaque, so there is no claim to
	 *  decode and the caller has to supply the email itself. Delete this (with `decodeJwtEmail`)
	 *  once the chatbot routes authenticate and take the email from the verified token instead. */
	email?: string;
	/** Extra headers merged into each request. */
	headers?: Record<string, string>;
	/** Override the API host. Defaults to the prod chatbot API; mainly for tests/playground. */
	baseUrl?: string;
}

interface ApiMessage {
	id: string;
	role: string;
	content: string;
	remote_session_id?: string | null;
	raw_response?: unknown;
	created_at: string;
}

interface ApiSession {
	id: string;
	email: string;
	title?: string | null;
	agent_type: string;
	remote_session_id?: string | null;
	created_at: string;
	last_activity_at: string;
	messages: ApiMessage[];
}

/** Decode the user's email from a JWT payload (the `pun` claim, falling back to `email`). */
export const decodeJwtEmail = (token?: string): string | undefined => {
	if (!token) return undefined;
	try {
		const payload = token.split(".")[1];
		if (!payload) return undefined;
		const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
		const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
		// SSR / Node fallback. Reached off globalThis rather than as a bare global so
		// this stays a browser package — pulling in @types/node for one call would put
		// every Node global in scope for the whole widget.
		const nodeBuffer = (
			globalThis as {
				Buffer?: { from(data: string, encoding: string): { toString(encoding: string): string } };
			}
		).Buffer;
		const json =
			typeof atob === "function" ? atob(padded) : nodeBuffer?.from(padded, "base64").toString("binary");
		if (json === undefined) return undefined;
		const claims = JSON.parse(json) as { pun?: unknown; email?: unknown };
		const value = claims.pun ?? claims.email;
		return typeof value === "string" ? value : undefined;
	} catch {
		return undefined;
	}
};

const toRole = (role: string): MessageRole => (role === "assistant" || role === "system" ? role : "user");

const STEP_STATUSES: readonly string[] = ["running", "success", "error", "cancelled"] satisfies ToolStep["status"][];

const isToolStep = (x: unknown): x is ToolStep => {
	const s = x as Partial<ToolStep> | null;
	return (
		typeof s?.id === "string" &&
		typeof s.name === "string" &&
		typeof s.status === "string" &&
		STEP_STATUSES.includes(s.status) &&
		(s.durationMs === undefined || typeof s.durationMs === "number")
	);
};

/** The trace a streamed reply saved as `{ done, steps }` in raw_response; absent for JSON agents. */
const stepsFromRaw = (raw: unknown): ToolStep[] | undefined => {
	let value = raw;
	if (typeof value === "string") {
		try {
			value = JSON.parse(value);
		} catch {
			return undefined;
		}
	}
	const steps = (value as { steps?: unknown } | null)?.steps;
	if (!Array.isArray(steps)) return undefined;
	const valid = steps.filter(isToolStep);
	return valid.length > 0 ? valid : undefined;
};

/**
 * Server-backed conversation store hitting the Parrot chatbot history API.
 * Sessions are scoped by the email decoded from `authToken` + `agentType`.
 */
export const createParrotHistoryStore = (config: ParrotHistoryStoreConfig): ConversationStore => {
	const { baseUrl = PARROT_HISTORY_BASE_URL, agentType, authToken, headers } = config;
	const email = config.email ?? decodeJwtEmail(authToken);
	const root = baseUrl.replace(/\/+$/, "");

	const requestHeaders = (): Record<string, string> => ({
		"Content-Type": "application/json",
		...(authToken ? { "x-auth-token": authToken } : {}),
		...(headers || {}),
	});

	const mapSession = (s: ApiSession): ChatSession => ({
		id: s.id,
		serverId: s.id,
		name: s.title || "New Chat",
		createdAt: s.created_at,
		lastActivityAt: s.last_activity_at,
		agentType: s.agent_type,
		remoteSessionId: s.remote_session_id || undefined,
		messages: (s.messages || []).map((m): ChatMessage => {
			const steps = m.role === "assistant" ? stepsFromRaw(m.raw_response) : undefined;
			return {
				id: m.id,
				role: toRole(m.role),
				content: m.content,
				createdAt: m.created_at,
				...(steps ? { steps } : {}),
			};
		}),
	});

	return {
		async list() {
			if (!email) return [];
			const url = `${root}/sessions/${encodeURIComponent(agentType)}?email=${encodeURIComponent(email)}`;
			const res = await fetch(url, { method: "GET", headers: requestHeaders() });
			if (!res.ok) throw new Error(`Failed to load sessions: ${res.status} ${res.statusText}`);
			const data = (await res.json()) as { sessions?: ApiSession[] };
			return (data.sessions || []).map(mapSession);
		},

		async recordExchange(input: RecordExchangeInput) {
			if (!email) throw new Error("Cannot persist conversation: no email claim in auth token.");

			const body: Record<string, unknown> = {
				email,
				agent_type: agentType,
				remote_session_id: input.remoteSessionId ?? "",
				user_message: input.userMessage,
				agent_message: input.agentMessage,
			};
			// First call creates the session (send title); later calls append (send id).
			if (input.sessionId) body.id = input.sessionId;
			else if (input.title) body.title = input.title;
			if (input.rawResponse !== undefined) {
				body.raw_response =
					typeof input.rawResponse === "string" ? input.rawResponse : JSON.stringify(input.rawResponse);
			}

			const res = await fetch(`${root}/sessions`, {
				method: "POST",
				headers: requestHeaders(),
				body: JSON.stringify(body),
			});
			if (!res.ok) throw new Error(`Failed to save exchange: ${res.status} ${res.statusText}`);
			const data = (await res.json()) as { session_id: string };
			return { sessionId: data.session_id };
		},

		async remove(sessionId: string) {
			const res = await fetch(`${root}/sessions/${encodeURIComponent(sessionId)}`, {
				method: "DELETE",
				headers: requestHeaders(),
			});
			if (!res.ok) throw new Error(`Failed to delete session: ${res.status} ${res.statusText}`);
		},
	};
};
